import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { isDefined, isNotDefined, type SetOptions } from '@enterprise/core';

import type { UseStorageOptions } from './types/use-storage.options';

/**
 * React hook for interacting with a storage adapter in a reactive way.
 *
 * This hook provides a value from the specified storage adapter, keeps it in sync with external changes,
 * and exposes an API for manipulating the storage (set, remove, clear namespace).
 *
 * @template TValue The type of the value stored.
 * @param options - The options for the storage hook.
 * @param options.adapter - The storage adapter implementing get, set, remove, subscribe, clearNamespace,
 * and destroy methods.
 * @param options.initialValue - The initial value to set if the key does not exist in storage.
 * @param options.key - The key under which the value is stored.
 * @returns A tuple containing the current value and an API object with methods to manipulate the storage:
 *   - `setValue(value, options)`: Sets the value for the key.
 *   - `remove()`: Removes the value for the key.
 *   - `clearNamespace()`: Clears all values in the adapter's namespace.
 *
 * @remarks
 * - The hook automatically subscribes to storage changes and updates the value reactively.
 * - On unmount, the adapter's `destroy` method is called for cleanup.
 * - If the key does not exist and an initial value is provided, it is set on mount.
 */
export const useStorage = <TValue = unknown>({ adapter, initialValue, key }: UseStorageOptions<TValue>) => {
  const getSnapshot = () => {
    const value = adapter.get(key);

    return value ?? initialValue;
  };

  const value = useSyncExternalStore(
    (cb) => {
      return adapter.subscribe(cb);
    },
    getSnapshot,
    getSnapshot
  );

  const api = useMemo(() => {
    return {
      clearNamespace: () => {
        adapter.clearNamespace();
      },
      remove: () => {
        adapter.remove(key);
      },
      setValue: (value: TValue | undefined, options?: SetOptions) => {
        adapter.set(key, value, options);
      },
    };
  }, [adapter, key]);

  useEffect(() => {
    const existing = adapter.get(key);

    if (isNotDefined(existing) && isDefined(initialValue)) {
      adapter.set(key, initialValue);
    }
  }, [adapter, key, initialValue]);

  return [value, api] as const;
};
